import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { SingleViewModeIcon, AllViewModeIcon, BatchViewModeIcon } from '@/UI/Icons';
import { I18N_KEYS } from '@/language/keys';
import { NodeViewMode } from '@/enums/node-view-mode.enum';
import { Flex, FlexCenHorVer } from '@/UI/styles';
import { NodeCarouselV2, type NodeCarouselV2Props } from '@/components/Common/ImageList/NodeCarousel';
import type { ReactNode } from 'react';

const validateNodeCarouselProps = (props: Partial<NodeCarouselV2Props>): props is NodeCarouselV2Props => {
  return (
    typeof props.steps === 'number' &&
    props.steps > 1 &&
    typeof props.selected === 'number' &&
    typeof props.handleBack === 'function' &&
    typeof props.handleNext === 'function'
  );
};

export interface ViewModeNavigationToolbarProps {
  isAllBatchesHaveSingleGeneration?: boolean;
  isSingleAssetMode: boolean;
  selectedViewMode?: NodeViewMode;
  setViewMode?: (viewMode: NodeViewMode) => void;
  isModelNode?: boolean;
  nodeCarouselProps: Partial<NodeCarouselV2Props>;
  filesNavigationComponent?: ReactNode;
}

export const ViewModeNavigationToolbar = ({
  isSingleAssetMode,
  selectedViewMode,
  setViewMode,
  isModelNode,
  nodeCarouselProps,
  filesNavigationComponent,
  isAllBatchesHaveSingleGeneration,
}: ViewModeNavigationToolbarProps) => {
  const { t } = useTranslation();
  const shouldIncludeBatch = !isAllBatchesHaveSingleGeneration && (isModelNode ?? false);
  const handleViewModeToggle = useCallback(() => {
    if (!setViewMode || !selectedViewMode) return;
    // Define the view mode rotation array based on whether Batch is included
    const viewModeRotation = shouldIncludeBatch
      ? [NodeViewMode.Single, NodeViewMode.Batch, NodeViewMode.All]
      : [NodeViewMode.Single, NodeViewMode.All];
    // Find current index, or default to 0 if not found (e.g., Batch mode when experiment is off)
    const currentIndex = viewModeRotation.indexOf(selectedViewMode);
    const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % viewModeRotation.length : 0;
    const nextViewMode = viewModeRotation[nextIndex];

    setViewMode(nextViewMode);
  }, [selectedViewMode, setViewMode, shouldIncludeBatch]);

  const onViewModeToggleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleViewModeToggle();
    },
    [handleViewModeToggle],
  );

  const viewModeIcon = useMemo(() => {
    switch (selectedViewMode) {
      case NodeViewMode.Single:
        return <SingleViewModeIcon />;
      case NodeViewMode.Batch:
        return <BatchViewModeIcon />;
      case NodeViewMode.All:
        return <AllViewModeIcon />;
      default:
        return null;
    }
  }, [selectedViewMode]);

  const viewModeTooltip = useMemo(() => {
    switch (selectedViewMode) {
      case NodeViewMode.Single:
        return t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.VIEW_MODE_SINGLE);
      case NodeViewMode.Batch:
        return t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.VIEW_MODE_BATCH);
      case NodeViewMode.All:
        return t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.VIEW_MODE_ALL);
      default:
        return '';
    }
  }, [selectedViewMode, t]);

  return (
    <Flex
      sx={{
        position: 'absolute',
        top: 8,
        left: 8,
        zIndex: 10,
        alignItems: 'center',
        gap: 0.5,
        pointerEvents: 'none',
      }}
    >
      <FlexCenHorVer sx={{ pointerEvents: 'all' }}>
        <Tooltip
          title={viewModeTooltip}
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -8],
                  },
                },
              ],
            },
          }}
          disableInteractive
        >
          <AppIconButton onClick={onViewModeToggleClick} width={32} height={32} mode="on-light">
            {viewModeIcon}
          </AppIconButton>
        </Tooltip>

        {isSingleAssetMode && validateNodeCarouselProps(nodeCarouselProps) ? (
          <NodeCarouselV2 {...nodeCarouselProps} />
        ) : null}
        {filesNavigationComponent}
      </FlexCenHorVer>
    </Flex>
  );
};
